import { BrowserRouter, Routes, Route } from 'react-router-dom';
import Layout from './components/Layout';

import Dashboard from './pages/Dashboard';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import VendorPortal from './pages/VendorPortal';
import VendorPortalDemo from './pages/VendorPortalDemo';
import NotFound from './pages/NotFound';

import Projects from './pages/Projects';
import AddProject from './pages/AddProject';
import EditProject from './pages/EditProject';
import ProjectCategory from './pages/ProjectCategory';
import AddProjectCategory from './pages/AddProjectCategory';
import EditProjectCategory from './pages/EditProjectCategory';
import UnitMaster from './pages/UnitMaster';
import AddUnitMaster from './pages/AddUnitMaster';
import EditUnitMaster from './pages/EditUnitMaster';
import ProjectSubCategory from './pages/ProjectSubCategory';
import AddProjectSubCategory from './pages/AddProjectSubCategory';
import EditProjectSubCategory from './pages/EditProjectSubCategory';
import ProjectSubCategoryDetail from './pages/ProjectSubCategoryDetail';
import AddProjectSubCategoryDetail from './pages/AddProjectSubCategoryDetail';
import EditProjectSubCategoryDetail from './pages/EditProjectSubCategoryDetail';
import Products from './pages/Products';
import AddProduct from './pages/AddProduct';
import EditProduct from './pages/EditProduct';
import Vendors from './pages/Vendors';
import AddVendor from './pages/AddVendor';
import EditVendor from './pages/EditVendor';
import Customers from './pages/Customers';
import AddCustomer from './pages/AddCustomer';
import EditCustomer from './pages/EditCustomer';
import Employees from './pages/Employees';
import AddEmployee from './pages/AddEmployee';
import EditEmployee from './pages/EditEmployee';
import PriorityManagement from './pages/PriorityManagement';
import PermissionRights from './pages/PermissionRights';
import Notifications from './pages/Notifications';
import GenerateRequests from './pages/GenerateRequests';
import AddGenerateRequest from './pages/AddGenerateRequest';
import EditGenerateRequest from './pages/EditGenerateRequest';
import ViewGenerateRequest from './pages/ViewGenerateRequest';
import InternalNotes from './pages/InternalNotes';
import ViewVendorQuotations from './pages/ViewVendorQuotations';
import VendorQuotesDetail from './pages/VendorQuotesDetail';
import CompareAuthorizedPO from './pages/CompareAuthorizedPO';
import ComparePODetail from './pages/ComparePODetail';
import ProductCategory from './pages/ProductCategory';
import AddProductCategory from './pages/AddProductCategory';
import EditProductCategory from './pages/EditProductCategory';
import POManagement from './pages/POManagement';
import POEntry from './pages/POEntry';
import InwardManagement from './pages/InwardManagement';
import InwardEntry from './pages/InwardEntry';
import EditInward from './pages/EditInward';
import ViewInward from './pages/ViewInward';
import PaymentManagement from './pages/PaymentManagement';
import AddPayment from './pages/AddPayment';
import EditPayment from './pages/EditPayment';
import ViewPayment from './pages/ViewPayment';
import WhatsappAuth from './pages/WhatsappAuth';
import ResetPassword from './pages/ResetPassword';
import Profile from './pages/Profile';
import NumbersManagement from './pages/NumbersManagement'
import AddNumber from './pages/AddNumber';
import EditNumber from './pages/EditNumber';
import ViewPODetail from './pages/ViewPODetail';

import VendorPaymentOverdueReport from './pages/VendorPaymentOverdueReport';
import ProjectExpenseReport from './pages/ProjectExpenseReport';
import ProjectSummaryReport from './pages/ProjectSummaryReport';
import ProjectVendorDetails from './pages/ProjectVendorDetails';
import ProjectPOList from './pages/ProjectPOList';
import ProjectVendorPODetails from './pages/ProjectVendorPODetails';
import VendorSummaryReport from './pages/VendorSummaryReport';
import VendorProjectSummary from './pages/VendorProjectSummary';
import VendorPOSummary from './pages/VendorPOSummary';
import PODetailView from './pages/PODetailView';
import ReportLog from './pages/ReportLog';
import HistoryLog from './pages/HistoryLog';
import PrintCompareQuotation from './pages/PrintCompareQuotation';

import ProtectedRoute from './components/routes/ProtectedRoute';
import PublicRoute from './components/routes/PublicRoute';
import { UserProvider } from './context/UserContext';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './App.css';

import { GlobalErrorProvider, useGlobalError } from './context/GlobalErrorContext';
import MaintenancePage from './pages/MaintenancePage';

function AppContent() {
  const { isServerError } = useGlobalError();

  if (isServerError) {
    return <MaintenancePage />;
  }

  return (
    <BrowserRouter>
      <UserProvider>
        <ToastContainer hideProgressBar={false} />
        <Routes>
          {/* 🔓 Public Routes */}
          <Route element={<PublicRoute />}>
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
          </Route>
          {/* 🔓 Vendor portal with two encrypted tokens: /encrypted_poId/encrypted_vendorId */}
          <Route path="/vendor-portal/:requestId/:vendorId" element={<VendorPortal />} />
          <Route path="/vendor-portal-demo" element={<VendorPortalDemo />} />
          {/* 🔒 Protected Routes */}
          <Route element={<ProtectedRoute />}>
            {/* Print Route (No Layout) */}
            <Route path="/print-quotation/:requestId" element={<PrintCompareQuotation />} />

            <Route path="/" element={<Layout />}>
              <Route index element={<Dashboard />} />
              <Route path="projects" element={<Projects />} />
              <Route path="projects/new" element={<AddProject />} />
              <Route path="projects/edit/:id" element={<EditProject />} />
              <Route path="projects/view/:id" element={<EditProject />} />
              <Route path="project-categories" element={<ProjectCategory />} />
              <Route path="project-categories/new" element={<AddProjectCategory />} />
              <Route path="project-categories/edit/:id" element={<EditProjectCategory />} />
              <Route path="project-categories/view/:id" element={<EditProjectCategory />} />
              <Route path="project-sub-categories" element={<ProjectSubCategory />} />
              <Route path="project-sub-categories/new" element={<AddProjectSubCategory />} />
              <Route path="project-sub-categories/edit/:id" element={<EditProjectSubCategory />} />
              <Route path="project-sub-categories/view/:id" element={<EditProjectSubCategory />} />
              <Route path="project-sub-category-details" element={<ProjectSubCategoryDetail />} />
              <Route path="project-sub-category-details/new" element={<AddProjectSubCategoryDetail />} />
              <Route path="project-sub-category-details/edit/:id" element={<EditProjectSubCategoryDetail />} />
              <Route path="project-sub-category-details/view/:id" element={<EditProjectSubCategoryDetail />} />
              <Route path="unit-master" element={<UnitMaster />} />
              <Route path="unit-master/new" element={<AddUnitMaster />} />
              <Route path="unit-master/edit/:id" element={<EditUnitMaster />} />
              <Route path="unit-master/view/:id" element={<EditUnitMaster />} />
              <Route path="generate-requests" element={<GenerateRequests />} />
              <Route path="generate-requests/new" element={<AddGenerateRequest />} />
              <Route path="generate-requests/edit/:id" element={<EditGenerateRequest />} />
              <Route path="generate-requests/view/:id" element={<ViewGenerateRequest />} />
              <Route path="generate-requests/:id/notes" element={<InternalNotes />} />
              <Route path="view-quotations" element={<ViewVendorQuotations />} />
              <Route path="vendor-quotes/:requestId" element={<VendorQuotesDetail />} />
              <Route path="compare-po" element={<CompareAuthorizedPO />} />
              <Route path="compare-po/:requestId" element={<ComparePODetail />} />
              <Route path="products" element={<Products />} />
              <Route path="products/new" element={<AddProduct />} />
              <Route path="products/edit/:id" element={<EditProduct />} />
              <Route path="products/view/:id" element={<EditProduct />} />
              <Route path="vendors" element={<Vendors />} />
              <Route path="vendors/new" element={<AddVendor />} />
              <Route path="vendors/edit/:id" element={<EditVendor />} />
              <Route path="vendors/view/:id" element={<EditVendor />} />
              <Route path="customers" element={<Customers />} />
              <Route path="customers/new" element={<AddCustomer />} />
              <Route path="customers/edit/:id" element={<EditCustomer />} />
              <Route path="customers/view/:id" element={<EditCustomer />} />
              <Route path="employees" element={<Employees />} />
              <Route path="employees/new" element={<AddEmployee />} />
              <Route path="employees/edit/:id" element={<EditEmployee />} />
              <Route path="employees/view/:id" element={<EditEmployee />} />
              <Route path="notifications" element={<Notifications />} />
              <Route path="settings" element={<NumbersManagement />} />
              <Route path="settings/new" element={<AddNumber />} />
              <Route path="settings/edit/:id" element={<EditNumber />} />
              <Route path="settings/view/:id" element={<EditNumber />} />

              <Route path="priority-management" element={<PriorityManagement />} />
              <Route path="product-categories" element={<ProductCategory />} />
              <Route path="product-categories/new" element={<AddProductCategory />} />
              <Route path="product-categories/edit/:id" element={<EditProductCategory />} />
              <Route path="product-categories/view/:id" element={<EditProductCategory />} />
              <Route path="permission-rights" element={<PermissionRights />} />
              {/* <Route path="settings" element={<div className="p-8">Settings Page (Coming Soon)</div>} /> */}

              <Route path="reports/vendor-payment-overdue" element={<VendorPaymentOverdueReport />} />
              <Route path="reports/project-expense" element={<ProjectExpenseReport />} />
              <Route path="reports/project-summary" element={<ProjectSummaryReport />} />
              <Route path="reports/project/:id/vendors" element={<ProjectVendorDetails />} />
              <Route path="reports/project/:projectId/po-list" element={<ProjectPOList />} />
              <Route path="reports/project/:projectId/vendor/:vendorId/pos" element={<ProjectVendorPODetails />} />
              <Route path="reports/vendor-summary" element={<VendorSummaryReport />} />
              <Route path="reports/vendor/:vendorId/project-summary" element={<VendorProjectSummary />} />
              <Route path="reports/vendor/:vendorId/po-summary" element={<VendorPOSummary />} />
              <Route path="reports/po/details/:poId" element={<ViewPODetail />} />
              <Route path="reports/po/:poNumber" element={<PODetailView />} />
              <Route path="reports/log" element={<ReportLog />} />
              <Route path="history" element={<HistoryLog />} />

              <Route path="po-management" element={<POManagement />} />
              <Route path="inward-management" element={<InwardManagement />} />
              <Route path="inward/entry" element={<InwardEntry />} />
              <Route path="inward/entry/:id" element={<EditInward />} />
              <Route path="inward/view/:id" element={<ViewInward />} />
              <Route path="po/entry/:poId" element={<POEntry />} />
              <Route path="payment-management" element={<PaymentManagement />} />
              <Route path="payment-management/new" element={<AddPayment />} />
              <Route path="payment-management/edit/:id" element={<EditPayment />} />
              <Route path="payment-management/view/:id" element={<ViewPayment />} />
              <Route path="whatsapp-auth" element={<WhatsappAuth />} />
              <Route path="profile" element={<Profile />} />
              <Route path="reset-password" element={<ResetPassword />} />
            </Route>
          </Route>
          {/* 404 Not Found Page */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </UserProvider>
    </BrowserRouter>
  );
}

function App() {
  return (
    <GlobalErrorProvider>
      <AppContent />
    </GlobalErrorProvider>
  );
}

export default App;

